/* Which way a scroller is travelling, with enough hysteresis to be usable.
 *
 * The filter band hides while you read and returns when you come back (ADR 0017
 * §4). Every condition below is the difference between working and strobing, and
 * none of it is observable in a desktop browser — momentum scrolling reverses
 * direction by a pixel or two constantly, and both ends of the scroll rubber-band.
 *
 * The hysteresis lives here once; each caller wires its own scroller, because only
 * it knows which element that is — the workspace host for Library and Index, the
 * list's own scrollport for Map.
 */

import { BP_DESKTOP } from '../design/tokens.js';

/** Accumulated travel in one direction before it counts as a direction. */
const THRESHOLD = 10;
/** Scroll depth below which nothing ever hides, so the band is present at rest. */
const DEAD_ZONE = 64;
/** Slack for sub-pixel scroll heights when testing for the bottom. */
const EPS = 1;

export interface ScrollDirectionOptions {
  threshold?: number;
  deadZone?: number;
}

export interface ScrollDirectionWatcher {
  /**
   * Forget the direction budget and report shown.
   *
   * This has to reach the watcher's OWN state rather than only the caller's copy.
   * A second flag that the caller resets independently desynchronises the moment
   * a workspace is left while collapsed: the band comes back on re-entry, the
   * watcher still believes it is hidden, and the next swipe is swallowed as "no
   * change" until an incidental reverse delta flips it back.
   */
  reset(): void;
  stop(): void;
}

/**
 * Report when `target` starts travelling far enough in one direction to matter.
 *
 * `onChange(true)` means going deeper — hide. `onChange(false)` means coming back,
 * or being at rest near the top — show. Fires only on a change, so a caller can
 * assign straight to a reactive property.
 *
 * Returns a disposer.
 */
export function watchScrollDirection(
  target: HTMLElement,
  onChange: (hidden: boolean) => void,
  opts: ScrollDirectionOptions = {},
): ScrollDirectionWatcher {
  const threshold = opts.threshold ?? THRESHOLD;
  const deadZone = opts.deadZone ?? DEAD_ZONE;

  let last = Math.max(0, target.scrollTop);
  let travel = 0;
  let hidden = false;

  const set = (next: boolean): void => {
    if (next === hidden) return;
    hidden = next;
    travel = 0;
    onChange(hidden);
  };

  const onScroll = (): void => {
    // Negative scrollTop is an overscroll at the top, which belongs to
    // pull-to-refresh (ADR 0010) and is not a direction.
    const y = Math.max(0, target.scrollTop);

    // Inside the dead zone the band is always shown, which is what makes "you can
    // never be at the top with it hidden" true by construction rather than by rule.
    if (y <= deadZone) {
      last = y;
      travel = 0;
      set(false);
      return;
    }

    // A bounce at the far end springs back upward, which would otherwise read as
    // coming back and reveal the band at the bottom of the list.
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - EPS) {
      last = y;
      return;
    }

    const delta = y - last;
    last = y;
    if (delta === 0) return;
    // A reversal starts its own budget, so momentum jitter never accumulates.
    if (delta > 0 !== travel > 0) travel = 0;
    travel += delta;
    if (Math.abs(travel) >= threshold) set(travel > 0);
  };

  target.addEventListener('scroll', onScroll, { passive: true });
  return {
    reset() {
      travel = 0;
      last = Math.max(0, target.scrollTop);
      // Goes through `set`, so it reports only if it really was hidden — and the
      // watcher's own flag genuinely moves rather than being shadowed.
      set(false);
    },
    stop() {
      target.removeEventListener('scroll', onScroll);
    },
  };
}

/**
 * Call `cb` whenever the shell brings this workspace back to the front.
 *
 * Workspaces stay mounted and are toggled with `hidden`, so there is no lifecycle
 * callback for becoming active. Arriving somewhere should show you the controls
 * (ADR 0017 §4), and "you left with the band hidden" is not something anyone
 * remembers.
 *
 * Returns a disposer.
 */
export function onBecameVisible(host: HTMLElement, cb: () => void): () => void {
  let was = host.hasAttribute('hidden');
  const observer = new MutationObserver(() => {
    const now = host.hasAttribute('hidden');
    if (was && !now) cb();
    was = now;
  });
  observer.observe(host, { attributes: true, attributeFilter: ['hidden'] });
  return () => observer.disconnect();
}

/**
 * The whole collapse rule for one workspace's band, in a single call.
 *
 * Wraps the three things every caller would otherwise repeat: the direction
 * watcher, the reset when the workspace comes back to the front, and the
 * breakpoint guard. The guard is not decoration — above the breakpoint the band
 * stays sticky (ADR 0017 §4), and a stale `collapsed` there would also send
 * Index's kind headers under a band that is still on screen.
 *
 * Returns a disposer.
 */
export function watchBandCollapse(
  host: HTMLElement,
  scroller: HTMLElement,
  apply: (collapsed: boolean) => void,
  opts?: ScrollDirectionOptions & {
    /** Also called when the workspace returns to the front, for anything the
        band should shed on arrival — an open search field, say. */
    onReturn?: () => void;
  },
): ScrollDirectionWatcher {
  const mq = window.matchMedia(`(max-width: ${BP_DESKTOP - 1}px)`);
  // Mirrors the watcher rather than deciding anything: it exists only so a
  // breakpoint change can re-apply the gate without a scroll event. Every write
  // comes from the watcher's callback, so the two cannot drift.
  let hidden = false;
  const push = (): void => apply(mq.matches && hidden);
  const watcher = watchScrollDirection(
    scroller,
    (h) => {
      hidden = h;
      push();
    },
    opts,
  );
  const reset = (): void => {
    watcher.reset();
    push();
  };
  const stopVisible = onBecameVisible(host, () => {
    reset();
    opts?.onReturn?.();
  });
  mq.addEventListener('change', reset);
  return {
    // Exposed because callers have their own reasons to start over — closing a
    // search field, say, where the band was pinned while the list scrolled under
    // it. Going through the watcher is not optional: setting the caller's own flag
    // instead leaves the watcher believing it is hidden, and the next swipe is
    // swallowed as "no change".
    reset,
    stop() {
      watcher.stop();
      stopVisible();
      mq.removeEventListener('change', reset);
    },
  };
}
